import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';
import { roleCoverage } from '@/utils/roles';
import type { PermissionGroup, RoleRecord } from '@/types/portal';

interface RoleListProps {
  roles: RoleRecord[];
  selectedId: string;
  catalog: PermissionGroup[];
  canManage: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
}

export default function RoleList({
  roles,
  selectedId,
  catalog,
  canManage,
  onSelect,
  onNew,
}: RoleListProps) {
  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">Roles</h3>
          <p className="mt-0.5 text-xs text-foreground-500">{roles.length} roles in this instance</p>
        </div>
        <Button size="sm" icon="ri-add-line" disabled={!canManage} onClick={onNew}>
          New role
        </Button>
      </header>

      <ul className="divide-y divide-background-100">
        {roles.map((role) => {
          const coverage = roleCoverage(role, catalog);
          const active = role.id === selectedId;
          return (
            <li key={role.id}>
              <button
                type="button"
                onClick={() => onSelect(role.id)}
                className={`w-full px-5 py-4 text-left transition-colors cursor-pointer ${
                  active ? 'bg-primary-50' : 'hover:bg-background-100'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-md text-base ${
                        active ? 'bg-primary-100 text-primary-700' : 'bg-background-200 text-foreground-600'
                      }`}
                    >
                      <i className="ri-shield-user-line" />
                    </span>
                    <span className="font-heading text-sm font-semibold text-foreground-950">
                      {role.name}
                    </span>
                  </span>
                  {role.isSystem ? (
                    <Badge tone="outline">System</Badge>
                  ) : (
                    <Badge tone="secondary">Custom</Badge>
                  )}
                </div>
                <p className="mt-1.5 line-clamp-2 text-xs text-foreground-500">{role.description}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-group-line" />
                    {role.memberCount} member{role.memberCount === 1 ? '' : 's'}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-key-2-line" />
                    {coverage.granted} permissions
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-scales-3-line" />
                    {role.approvalsCeiling}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}